import {
  type CSSProperties,
  type DragEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  ArrowUpToLine,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Eye,
  Film,
  ImageIcon,
  ImagePlus,
  Pencil,
  Pin,
  PinOff,
  Play,
  Send,
  SquarePen,
  Trash2,
  X,
} from "lucide-react";
import {
  type ChannelDetail,
  type Delivery,
  type DeliveryAction,
  type PostMedia,
  type WriteWhen,
  type WrittenFile,
  pictureSrc,
  useDeliveries,
  useDeliveryAction,
  useDropMedia,
  useMoveDelivery,
  usePinDelivery,
  useWritePost,
} from "@/lib/aggregator";
import { cn } from "@/lib/cn";
import { formatMoment, formatNumber } from "@/lib/format";
import { pushToast } from "@/components/Toaster";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ConfirmDialog,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ActionMenu, type ActionMenuItem } from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SimpleSelect } from "@/components/ui/select";
import { TEXTAREA, errorText } from "./helpers";
import { RichTextView } from "./parts";
import { EditDialog } from "./PostsTab";

const PAGE = 20;
const UPCOMING = 50;
// What a post of one's own may carry — the Bot API's own limits for uploads.
const FILES_MAX = 10;
const PHOTO_MAX_MB = 10;
const VIDEO_MAX_MB = 50;
const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
const VIDEO_TYPES = ["video/mp4"];

/**
 * The channel as its readers see it: what goes out next, on top, and below
 * what is in the channel, the latest first — each post as Telegram shows it,
 * with what a person may do to it.
 */
export function FeedTab({
  channel,
  active,
  canEdit,
  onOpenTab,
}: {
  channel: ChannelDetail;
  /** The tab is on screen: only then is the feed kept fresh. */
  active: boolean;
  canEdit: boolean;
  onOpenTab: (tab: string) => void;
}) {
  const { t } = useTranslation();
  const [limit, setLimit] = useState(PAGE);
  const upcoming = useDeliveries(active ? channel.id : null, "queue", { limit: UPCOMING }, active);
  const out = useDeliveries(active ? channel.id : null, "published", { limit }, active);
  const act = useDeliveryAction();
  const move = useMoveDelivery();
  const pin = usePinDelivery();
  const drop = useDropMedia();
  const [writing, setWriting] = useState(false);
  const [editing, setEditing] = useState<Delivery | null>(null);
  const [viewing, setViewing] = useState<{ delivery: Delivery; index: number } | null>(null);
  const [dropping, setDropping] = useState<{ delivery: Delivery; index: number } | null>(null);
  const [confirming, setConfirming] = useState<{
    delivery: Delivery;
    action: DeliveryAction;
  } | null>(null);

  const said = async (work: () => Promise<unknown>, done: string) => {
    try {
      await work();
      pushToast({ kind: "success", text: done });
    } catch (err) {
      pushToast({ kind: "error", text: errorText(err, t("aggregator.action_error")) });
    }
  };
  const run = (delivery: Delivery, action: DeliveryAction) =>
    said(() => act.mutateAsync({ delivery, action }), t(`aggregator.post.done.${action}`));

  const waiting = upcoming.data?.items ?? [];
  const published = out.data?.items ?? [];
  const loading = upcoming.isLoading || out.isLoading;
  const nothing = !loading && waiting.length === 0 && published.length === 0;

  return (
    <div className="space-y-4">
      {channel.status === "active" && channel.problem && (
        <Alert tone="warning">{t("aggregator.queue.held")}</Alert>
      )}
      {channel.status === "paused" && <Alert tone="warning">{t("aggregator.queue.paused")}</Alert>}
      {channel.review > 0 && (
        <Alert
          tone="info"
          action={
            <Button size="sm" variant="outline" onClick={() => onOpenTab("review")}>
              {t("aggregator.feed.to_review")}
            </Button>
          }
        >
          {t("aggregator.feed.review_waits", { count: channel.review })}
        </Alert>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs text-fg-muted">
          {upcoming.data && out.data
            ? t("aggregator.feed.counts", {
                waiting: upcoming.data.total,
                published: out.data.total,
              })
            : ""}
        </p>
        {canEdit && (
          <Button
            size="sm"
            className="ml-auto"
            onClick={() => setWriting(true)}
            iconLeft={<SquarePen size={15} strokeWidth={2} aria-hidden="true" />}
          >
            {t("aggregator.feed.write")}
          </Button>
        )}
      </div>

      {loading && <p className="text-sm text-fg-muted">{t("aggregator.loading")}</p>}
      {(upcoming.isError || out.isError) && (
        <Alert tone="danger">{t("aggregator.load_error")}</Alert>
      )}
      {nothing && (
        <EmptyState
          icon={Send}
          title={t("aggregator.feed.empty_title")}
          description={t("aggregator.feed.empty_hint")}
        />
      )}

      {!nothing && (
        <div className="rounded-2xl bg-bg-elevated/40 px-2 py-4 sm:px-5">
          <div className="mx-auto max-w-xl space-y-6">
            {waiting.length > 0 && (
              <section className="space-y-3">
                <SectionMark label={t("aggregator.feed.upcoming")} n={upcoming.data?.total} />
                <ol className="space-y-5">
                  {waiting.map((delivery, n) => (
                    <FeedPost
                      key={delivery.id}
                      delivery={delivery}
                      channel={channel}
                      canEdit={canEdit}
                      onView={(index) => setViewing({ delivery, index })}
                      onDrop={(index) => setDropping({ delivery, index })}
                      actions={
                        canEdit &&
                        (delivery.status === "queued" || delivery.status === "pending") && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setConfirming({ delivery, action: "publish-now" })}
                              iconLeft={<Send size={14} strokeWidth={2} aria-hidden="true" />}
                            >
                              {t("aggregator.post.publish_now")}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditing(delivery)}
                              iconLeft={<Pencil size={14} strokeWidth={2} aria-hidden="true" />}
                            >
                              {t("aggregator.post.edit")}
                            </Button>
                            <ActionMenu
                              label={t("aggregator.feed.more_actions")}
                              items={[
                                ...(delivery.status === "queued" && n > 0 && !delivery.publish_at
                                  ? [
                                      {
                                        label: t("aggregator.queue.move_top"),
                                        icon: <ArrowUpToLine size={15} aria-hidden="true" />,
                                        onSelect: () =>
                                          said(
                                            () => move.mutateAsync({ delivery, to: "top" }),
                                            t("aggregator.feed.raised"),
                                          ),
                                      },
                                    ]
                                  : []),
                                {
                                  label: t("aggregator.post.reject"),
                                  icon: <Trash2 size={15} aria-hidden="true" />,
                                  destructive: true,
                                  separatorBefore: true,
                                  onSelect: () => run(delivery, "reject"),
                                },
                              ]}
                            />
                          </>
                        )
                      }
                    />
                  ))}
                </ol>
              </section>
            )}

            {published.length > 0 && (
              <section className="space-y-3">
                <SectionMark label={t("aggregator.feed.in_channel")} n={out.data?.total} />
                <ol className="space-y-5">
                  {published.map((delivery) => {
                    const menu: ActionMenuItem[] = [
                      {
                        label: t(delivery.pinned ? "aggregator.feed.unpin" : "aggregator.feed.pin"),
                        icon: delivery.pinned ? (
                          <PinOff size={15} aria-hidden="true" />
                        ) : (
                          <Pin size={15} aria-hidden="true" />
                        ),
                        onSelect: () =>
                          said(
                            () => pin.mutateAsync({ delivery, pinned: !delivery.pinned }),
                            t(
                              delivery.pinned
                                ? "aggregator.feed.unpinned"
                                : "aggregator.feed.pinned_done",
                            ),
                          ),
                      },
                      ...(delivery.url
                        ? [
                            {
                              label: t("aggregator.post.open"),
                              icon: <ExternalLink size={15} aria-hidden="true" />,
                              onSelect: () =>
                                window.open(delivery.url ?? "", "_blank", "noopener,noreferrer"),
                            },
                          ]
                        : []),
                      {
                        label: t("aggregator.post.unpublish"),
                        icon: <Trash2 size={15} aria-hidden="true" />,
                        destructive: true,
                        separatorBefore: true,
                        onSelect: () => setConfirming({ delivery, action: "unpublish" }),
                      },
                    ];
                    return (
                      <FeedPost
                        key={delivery.id}
                        delivery={delivery}
                        channel={channel}
                        canEdit={canEdit}
                        onView={(index) => setViewing({ delivery, index })}
                        onDrop={(index) => setDropping({ delivery, index })}
                        actions={
                          canEdit && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditing(delivery)}
                                iconLeft={<Pencil size={14} strokeWidth={2} aria-hidden="true" />}
                              >
                                {t("aggregator.post.edit")}
                              </Button>
                              <ActionMenu label={t("aggregator.feed.more_actions")} items={menu} />
                            </>
                          )
                        }
                      />
                    );
                  })}
                </ol>
                {out.data && out.data.total > published.length && (
                  <div className="flex justify-center pt-1">
                    <Button variant="outline" size="sm" onClick={() => setLimit((n) => n + PAGE)}>
                      {t("aggregator.posts.more")}
                    </Button>
                  </div>
                )}
              </section>
            )}
          </div>
        </div>
      )}

      <WriteDialog channel={channel} open={writing} onClose={() => setWriting(false)} />
      <EditDialog delivery={editing} onClose={() => setEditing(null)} />
      <Viewer
        shown={viewing}
        onClose={() => setViewing(null)}
        onIndex={(index) => setViewing((v) => v && { ...v, index })}
      />
      <ConfirmDialog
        open={dropping !== null}
        onOpenChange={(o) => !o && setDropping(null)}
        tone="danger"
        title={t("aggregator.feed.drop_title")}
        description={t(
          dropping?.delivery.status === "published"
            ? "aggregator.feed.drop_hint_published"
            : "aggregator.feed.drop_hint_queued",
        )}
        confirmText={t("aggregator.feed.drop")}
        cancelText={t("common.cancel")}
        onConfirm={async () => {
          if (!dropping) return;
          const { delivery, index } = dropping;
          await said(
            () => drop.mutateAsync({ delivery, index, url: delivery.media[index].url }),
            t("aggregator.feed.dropped"),
          );
        }}
      />
      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(o) => !o && setConfirming(null)}
        tone={confirming?.action === "unpublish" ? "danger" : "default"}
        title={t(`aggregator.post.confirm.${confirming?.action ?? "publish-now"}`)}
        description={t(`aggregator.feed.confirm_hint.${confirming?.action ?? "publish-now"}`)}
        confirmText={t(
          confirming?.action === "unpublish"
            ? "aggregator.post.unpublish"
            : "aggregator.post.publish_now",
        )}
        cancelText={t("common.cancel")}
        onConfirm={async () => {
          if (confirming) await run(confirming.delivery, confirming.action);
        }}
      />
    </div>
  );
}

function SectionMark({ label, n }: { label: string; n: number | undefined }) {
  return (
    <div className="flex items-center gap-3">
      <span className="h-px flex-1 bg-border" />
      <span className="rounded-full bg-bg-primary px-3 py-0.5 text-xs font-medium text-fg-secondary shadow-sm">
        {label}
        {n ? <span className="ml-1.5 font-mono text-fg-muted">{n}</span> : null}
      </span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

/* --------------------------------------------------------------- a post */

function FeedPost({
  delivery,
  channel,
  canEdit,
  onView,
  onDrop,
  actions,
}: {
  delivery: Delivery;
  channel: ChannelDetail;
  canEdit: boolean;
  onView: (index: number) => void;
  onDrop: (index: number) => void;
  actions: ReactNode;
}) {
  const { t } = useTranslation();
  const text = delivery.text ?? delivery.original;
  const out = delivery.status === "published";
  const when = out
    ? delivery.published_at && formatMoment(delivery.published_at, channel.timezone)
    : delivery.publish_at
      ? t("aggregator.feed.at", { when: formatMoment(delivery.publish_at, channel.timezone) })
      : delivery.expected_at
        ? t("aggregator.queue.expected", {
            when: formatMoment(delivery.expected_at, channel.timezone),
          })
        : t(delivery.status === "pending" ? "aggregator.feed.preparing" : "aggregator.feed.waits");
  // A file can go while more than it is left: a lone file of a post with no
  // text is the post itself.
  const droppable = canEdit && (delivery.media.length > 1 || (!out && text.markup.trim() !== ""));
  return (
    <li className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-1 text-xs text-fg-muted">
        {delivery.own ? (
          <Badge tone="accent">{t("aggregator.feed.own")}</Badge>
        ) : (
          <a
            href={delivery.source_url}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex min-w-0 items-center gap-1 hover:underline"
          >
            <span className="truncate">
              {delivery.source_title ?? `@${delivery.source_username}`}
            </span>
            <ExternalLink size={11} strokeWidth={2} aria-hidden="true" className="shrink-0" />
          </a>
        )}
        {delivery.fill_id && <Badge>{t("aggregator.post.from_fill")}</Badge>}
        {delivery.publish_at && !out && <Badge tone="info">{t("aggregator.feed.timed")}</Badge>}
        {delivery.pinned && (
          <span className="inline-flex items-center gap-1 text-accent">
            <Pin size={12} strokeWidth={2} aria-hidden="true" />
            {t("aggregator.feed.pinned")}
          </span>
        )}
      </div>
      <article
        className={cn(
          "overflow-hidden rounded-2xl border bg-bg-primary shadow-sm",
          out ? "border-border" : "border-dashed border-border",
        )}
      >
        {delivery.media.length > 0 && (
          <MediaGrid
            media={delivery.media}
            t={t}
            onOpen={onView}
            onDrop={droppable ? onDrop : undefined}
          />
        )}
        {delivery.media_missing.length > 0 && (
          <p className="px-3.5 pt-2 text-xs text-warning">
            {t("aggregator.media.missing", {
              what: delivery.media_missing
                .map((kind) => t(`aggregator.media.missing_kind.${kind}`, kind))
                .join(", "),
            })}
          </p>
        )}
        {text.markup.trim() !== "" && (
          <div className="px-3.5 pt-2.5">
            <RichTextView text={text} clamp />
          </div>
        )}
        <div className="flex items-center justify-end gap-2.5 px-3.5 pb-2 pt-1.5 text-[11px] text-fg-muted">
          {/* (A post of one's own is the person's text to begin with.) */}
          {delivery.edited && !delivery.own && <span>{t("aggregator.feed.edited")}</span>}
          {out && delivery.views !== null && (
            <span className="inline-flex items-center gap-1">
              <Eye size={12} strokeWidth={2} aria-hidden="true" />
              {formatNumber(delivery.views)}
            </span>
          )}
          <span className={cn(!out && "text-fg-secondary")}>{when}</span>
        </div>
      </article>
      {actions && <div className="flex flex-wrap items-center justify-end gap-1">{actions}</div>}
    </li>
  );
}

/* ------------------------------------------------------- an album, laid out */

/** How Telegram lays an album out, near enough: rows of two and three. */
function rowsOf(n: number): number[] {
  if (n <= 2) return [n];
  if (n === 3) return [1, 2];
  if (n === 4) return [2, 2];
  const rows: number[] = [];
  let left = n;
  if (n % 3 === 1) {
    rows.push(2, 2);
    left -= 4;
  } else if (n % 3 === 2) {
    rows.push(2);
    left -= 2;
  }
  while (left > 0) {
    rows.push(3);
    left -= 3;
  }
  return rows;
}

function shape(item: PostMedia): number {
  const ratio =
    item.width && item.height ? item.width / item.height : item.kind === "photo" ? 1 : 16 / 9;
  return Math.min(2, Math.max(0.5, ratio));
}

function duration(seconds: number | null | undefined): string | null {
  if (!seconds) return null;
  const m = Math.floor(seconds / 60);
  const s = String(Math.floor(seconds % 60)).padStart(2, "0");
  return m >= 60 ? `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}:${s}` : `${m}:${s}`;
}

function MediaGrid({
  media,
  t,
  onOpen,
  onDrop,
}: {
  media: PostMedia[];
  t: TFunction;
  onOpen: (index: number) => void;
  onDrop?: (index: number) => void;
}) {
  if (media.length === 1) {
    const ratio = Math.min(1.9, Math.max(0.6, shape(media[0])));
    return (
      <div
        className="flex max-h-[30rem] justify-center bg-bg-elevated"
        style={{ aspectRatio: String(ratio) }}
      >
        <Tile item={media[0]} index={0} t={t} big onOpen={onOpen} onDrop={onDrop} />
      </div>
    );
  }
  let at = 0;
  return (
    <div className="flex flex-col gap-0.5">
      {rowsOf(media.length).map((count, row) => {
        const items = media.slice(at, at + count).map((item, n) => ({ item, index: at + n }));
        at += count;
        const total = items.reduce((sum, { item }) => sum + shape(item), 0);
        return (
          <div key={row} className="flex gap-0.5" style={{ aspectRatio: String(total) }}>
            {items.map(({ item, index }) => (
              <Tile
                key={index}
                item={item}
                index={index}
                t={t}
                big={media.length <= 2}
                style={{ flexGrow: shape(item), flexBasis: 0 }}
                onOpen={onOpen}
                onDrop={onDrop}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function Tile({
  item,
  index,
  t,
  big,
  style,
  onOpen,
  onDrop,
}: {
  item: PostMedia;
  index: number;
  t: TFunction;
  /** Shown large: the full picture rather than the small one. */
  big: boolean;
  style?: CSSProperties;
  onOpen: (index: number) => void;
  onDrop?: (index: number) => void;
}) {
  const [broken, setBroken] = useState(false);
  const src = item.kind === "photo" ? (big || !item.thumb ? item.url : item.thumb) : item.thumb;
  const length = duration(item.duration);
  return (
    <div className="group relative min-w-0 flex-1 overflow-hidden bg-bg-elevated" style={style}>
      <button
        type="button"
        className="block h-full w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
        onClick={() => onOpen(index)}
        aria-label={t(`aggregator.media.${item.kind}`)}
      >
        {src && !broken ? (
          <img
            src={pictureSrc(src)}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setBroken(true)}
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-fg-muted">
            {item.kind === "photo" ? (
              <ImageIcon size={28} strokeWidth={1.5} aria-hidden="true" />
            ) : (
              <Film size={28} strokeWidth={1.5} aria-hidden="true" />
            )}
          </span>
        )}
        {item.kind !== "photo" && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="rounded-full bg-black/55 p-3 text-white">
              <Play size={big ? 22 : 16} strokeWidth={2} fill="currentColor" aria-hidden="true" />
            </span>
          </span>
        )}
        {(length || item.kind === "gif") && (
          <span className="pointer-events-none absolute bottom-1.5 left-1.5 rounded-md bg-black/55 px-1.5 py-0.5 text-[11px] font-medium text-white">
            {item.kind === "gif" ? "GIF" : length}
          </span>
        )}
      </button>
      {onDrop && (
        <button
          type="button"
          onClick={() => onDrop(index)}
          title={t("aggregator.feed.drop")}
          aria-label={t("aggregator.feed.drop")}
          className="absolute right-1.5 top-1.5 rounded-full bg-black/55 p-1 text-white opacity-100 transition-opacity hover:bg-danger focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:opacity-0 sm:group-hover:opacity-100"
        >
          <X size={14} strokeWidth={2.5} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- a viewer */

function Viewer({
  shown,
  onClose,
  onIndex,
}: {
  shown: { delivery: Delivery; index: number } | null;
  onClose: () => void;
  onIndex: (index: number) => void;
}) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  const media = shown?.delivery.media ?? [];
  const index = shown?.index ?? 0;
  const item = media[index];
  useEffect(() => setFailed(false), [shown?.delivery.id, index]);
  const step = (by: number) => onIndex((index + by + media.length) % media.length);
  return (
    <Dialog open={shown !== null && item !== undefined} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="xl" className="bg-black/95 text-white">
        <DialogTitle className="sr-only">{t("aggregator.feed.viewer")}</DialogTitle>
        {item && (
          <div
            className="relative flex min-h-[40vh] items-center justify-center"
            onKeyDown={(e) => {
              if (e.key === "ArrowRight") step(1);
              if (e.key === "ArrowLeft") step(-1);
            }}
          >
            {failed ? (
              <div className="space-y-3 p-10 text-center text-sm text-white/80">
                <p>{t("aggregator.feed.no_preview")}</p>
                {shown?.delivery.url && (
                  <a
                    href={shown.delivery.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1.5 text-white underline"
                  >
                    <ExternalLink size={14} aria-hidden="true" />
                    {t("aggregator.post.open")}
                  </a>
                )}
              </div>
            ) : item.small && item.kind !== "photo" ? (
              // Only its cover is kept here: the video plays in Telegram.
              <div className="space-y-3 text-center">
                {item.thumb && (
                  <img
                    src={pictureSrc(item.thumb)}
                    alt=""
                    className="mx-auto max-h-[70vh] w-full object-contain"
                  />
                )}
                <p className="text-sm text-white/80">{t("aggregator.feed.video_in_telegram")}</p>
                {shown?.delivery.url && (
                  <a
                    href={shown.delivery.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1.5 text-white underline"
                  >
                    <ExternalLink size={14} aria-hidden="true" />
                    {t("aggregator.post.open")}
                  </a>
                )}
              </div>
            ) : item.kind === "photo" ? (
              <img
                src={pictureSrc(item.url)}
                alt=""
                referrerPolicy="no-referrer"
                onError={() => setFailed(true)}
                className="max-h-[80vh] w-full object-contain"
              />
            ) : (
              <video
                key={item.url}
                src={pictureSrc(item.url)}
                poster={item.thumb ? pictureSrc(item.thumb) : undefined}
                controls
                autoPlay
                loop={item.kind === "gif"}
                onError={() => setFailed(true)}
                className="max-h-[80vh] w-full"
              />
            )}
            {media.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => step(-1)}
                  aria-label={t("aggregator.feed.previous")}
                  className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 hover:bg-white/20"
                >
                  <ChevronLeft size={22} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => step(1)}
                  aria-label={t("aggregator.feed.next")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 hover:bg-white/20"
                >
                  <ChevronRight size={22} aria-hidden="true" />
                </button>
                <span className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-2.5 py-0.5 text-xs">
                  {index + 1} / {media.length}
                </span>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------ a post of one's own */

type Picked = WrittenFile & { id: string; preview: string | null };

/** A video's frame, length and a still for its cover — read here, in the
 *  browser: told nothing, Telegram lays a video out as big as the screen
 *  and shows 0:00 on it. */
function readVideo(
  file: File,
): Promise<Pick<WrittenFile, "width" | "height" | "duration" | "cover">> {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    const address = URL.createObjectURL(file);
    let done = false;
    const finish = (found: Pick<WrittenFile, "width" | "height" | "duration" | "cover">) => {
      if (done) return;
      done = true;
      URL.revokeObjectURL(address);
      resolve(found);
    };
    const giveUp = window.setTimeout(() => finish({}), 8000);
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    video.onloadedmetadata = () => {
      video.currentTime = Math.min(1, (video.duration || 0) / 2);
    };
    video.onseeked = () => {
      const width = video.videoWidth;
      const height = video.videoHeight;
      const seconds = Math.round(video.duration || 0);
      const canvas = document.createElement("canvas");
      const scale = Math.min(1, 320 / Math.max(width, height, 1));
      canvas.width = Math.max(1, Math.round(width * scale));
      canvas.height = Math.max(1, Math.round(height * scale));
      canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (cover) => {
          window.clearTimeout(giveUp);
          finish({
            width: width || undefined,
            height: height || undefined,
            duration: seconds || undefined,
            cover: cover ?? undefined,
          });
        },
        "image/jpeg",
        0.8,
      );
    };
    video.onerror = () => {
      window.clearTimeout(giveUp);
      finish({});
    };
    video.src = address;
  });
}

function localInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function WriteDialog({
  channel,
  open,
  onClose,
}: {
  channel: ChannelDetail;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const write = useWritePost();
  const [text, setText] = useState("");
  const [files, setFiles] = useState<Picked[]>([]);
  const [when, setWhen] = useState<WriteWhen>("next");
  const [at, setAt] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [reading, setReading] = useState(0);
  const picker = useRef<HTMLInputElement>(null);
  // The previews are the page's own addresses: given back when done with.
  const previews = useRef<string[]>([]);
  const preview = (blob: Blob) => {
    const address = URL.createObjectURL(blob);
    previews.current.push(address);
    return address;
  };
  const letGo = () => {
    for (const address of previews.current) URL.revokeObjectURL(address);
    previews.current = [];
  };
  useEffect(() => letGo, []);

  useEffect(() => {
    if (!open) return;
    letGo();
    setText("");
    setFiles([]);
    setWhen("next");
    setAt(localInput(new Date(Date.now() + 60 * 60 * 1000)));
    setError(null);
  }, [open]);

  const add = async (list: FileList | null) => {
    if (!list) return;
    setError(null);
    const chosen = Array.from(list);
    if (files.length + chosen.length > FILES_MAX) {
      setError(t("aggregator.write.too_many", { n: FILES_MAX }));
      return;
    }
    for (const file of chosen) {
      const kind = PHOTO_TYPES.includes(file.type)
        ? "photo"
        : VIDEO_TYPES.includes(file.type)
          ? "video"
          : null;
      if (kind === null) {
        setError(t("aggregator.write.bad_type", { name: file.name }));
        return;
      }
      const limit = kind === "photo" ? PHOTO_MAX_MB : VIDEO_MAX_MB;
      if (file.size > limit * 1024 * 1024) {
        setError(t("aggregator.write.too_big", { name: file.name, mb: limit }));
        return;
      }
    }
    setReading((n) => n + 1);
    try {
      const picked: Picked[] = [];
      for (const file of chosen) {
        const id = `${file.name}:${file.size}:${Math.random().toString(36).slice(2)}`;
        if (PHOTO_TYPES.includes(file.type)) {
          picked.push({ id, file, kind: "photo", preview: preview(file) });
        } else {
          const frame = await readVideo(file);
          picked.push({
            id,
            file,
            kind: "video",
            ...frame,
            preview: frame.cover ? preview(frame.cover) : null,
          });
        }
      }
      setFiles((held) => [...held, ...picked]);
    } finally {
      setReading((n) => n - 1);
    }
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    void add(event.dataTransfer.files);
  };

  const submit = async () => {
    setError(null);
    if (!text.trim() && files.length === 0) {
      setError(t("aggregator.write.empty"));
      return;
    }
    let moment: string | null = null;
    if (when === "at") {
      const chosen = new Date(at);
      if (Number.isNaN(chosen.getTime()) || chosen.getTime() < Date.now() + 60_000) {
        setError(t("aggregator.write.past"));
        return;
      }
      moment = chosen.toISOString();
    }
    try {
      await write.mutateAsync({ channelId: channel.id, text, when, at: moment, files });
      pushToast({
        kind: "success",
        text:
          when === "at" && moment
            ? t("aggregator.write.done_at", { when: formatMoment(moment, channel.timezone) })
            : t(`aggregator.write.done_${when}`),
      });
      onClose();
    } catch (err) {
      setError(errorText(err, t("aggregator.action_error")));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t("aggregator.write.title")}</DialogTitle>
          <DialogDescription>{t("aggregator.write.hint")}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-1.5">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={7}
              maxLength={12000}
              className={TEXTAREA}
              placeholder={t("aggregator.write.placeholder")}
              aria-label={t("aggregator.post.text")}
            />
            <p className="text-xs text-fg-muted">{t("aggregator.markup_hint")}</p>
          </div>

          <div
            className="space-y-2 rounded-xl border border-dashed border-border p-3"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
          >
            {files.length > 0 && (
              <ul className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                {files.map((picked) => (
                  <li
                    key={picked.id}
                    className="relative aspect-square overflow-hidden rounded-lg border border-border bg-bg-elevated"
                  >
                    {picked.preview ? (
                      <img src={picked.preview} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center text-fg-muted">
                        <Film size={22} aria-hidden="true" />
                      </span>
                    )}
                    {picked.kind === "video" && (
                      <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 text-[10px] text-white">
                        {duration(picked.duration) ?? t("aggregator.media.video")}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => setFiles((held) => held.filter((f) => f.id !== picked.id))}
                      aria-label={t("aggregator.write.remove")}
                      className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white hover:bg-danger"
                    >
                      <X size={13} strokeWidth={2.5} aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                loading={reading > 0}
                disabled={files.length >= FILES_MAX}
                onClick={() => picker.current?.click()}
                iconLeft={<ImagePlus size={15} strokeWidth={2} aria-hidden="true" />}
              >
                {t("aggregator.write.add_files")}
              </Button>
              <span className="text-xs text-fg-muted">
                {t("aggregator.write.files_hint", {
                  n: FILES_MAX,
                  photo: PHOTO_MAX_MB,
                  video: VIDEO_MAX_MB,
                })}
              </span>
              <input
                ref={picker}
                type="file"
                multiple
                accept={[...PHOTO_TYPES, ...VIDEO_TYPES].join(",")}
                className="hidden"
                onChange={(e) => {
                  void add(e.target.files);
                  e.target.value = "";
                }}
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("aggregator.write.when")}</Label>
              <SimpleSelect
                value={when}
                onValueChange={(value) => setWhen(value as WriteWhen)}
                options={(["next", "now", "at"] as const).map((value) => ({
                  value,
                  label: t(`aggregator.write.when_${value}`),
                }))}
              />
            </div>
            {when === "at" && (
              <div className="space-y-1.5">
                <Label htmlFor="agg_write_at">{t("aggregator.write.at")}</Label>
                <Input
                  id="agg_write_at"
                  type="datetime-local"
                  value={at}
                  min={localInput(new Date(Date.now() + 2 * 60_000))}
                  onChange={(e) => setAt(e.target.value)}
                />
              </div>
            )}
          </div>
          {channel.status !== "active" && when !== "now" && (
            <Alert tone="warning">{t("aggregator.write.channel_paused")}</Alert>
          )}
          {error && <Alert tone="danger">{error}</Alert>}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={submit}
            loading={write.isPending}
            disabled={write.isPending || reading > 0}
            iconLeft={<Send size={14} strokeWidth={2} aria-hidden="true" />}
          >
            {t(`aggregator.write.submit_${when}`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
